import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  ArrowLeft,
  History,
  Pencil,
  CreditCard,
  Users,
  UserCircle,
  Package,
  ClipboardList,
  Clock,
  MessageSquare,
  FileText,
  ChevronDown,
  Sparkles,
  CalendarDays,
  Baby,
  Wallet,
  Receipt,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { GET_BOOKING, GET_BOOKING_CHANGE_LOGS } from '../../graphql/queries/booking';

type ChangeLogEntry = {
  id: number;
  bookingId: number;
  fieldKey: string;
  label: string;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
};

type FieldCategory =
  | 'statuts'
  | 'forfait'
  | 'voyageurs'
  | 'contact'
  | 'paiement'
  | 'autre';

const CATEGORY_LABEL: Record<FieldCategory, string> = {
  statuts: 'Statuts & suivi',
  forfait: 'Forfait & tarif',
  voyageurs: 'Voyageurs & chambres',
  contact: 'Contact',
  paiement: 'Paiement',
  autre: 'Autres informations',
};

const CATEGORY_ORDER: FieldCategory[] = [
  'statuts',
  'forfait',
  'voyageurs',
  'contact',
  'paiement',
  'autre',
];

function getCategory(fieldKey: string): FieldCategory {
  if (['status', 'statutReservation', 'statutDisponibilite', 'typeReservation'].includes(fieldKey)) {
    return 'statuts';
  }
  if (['packageId', 'formulaId', 'totalPrice', 'travelerCount'].includes(fieldKey)) {
    return 'forfait';
  }
  if (
    ['nbrAdulte', 'nbrEnfant', 'nbrBebe', 'nbrChambre', 'personnes', 'bebes', 'chambres'].includes(fieldKey)
  ) {
    return 'voyageurs';
  }
  if (['nom', 'prenom', 'telephone', 'mail', 'contactInfo'].includes(fieldKey)) {
    return 'contact';
  }
  if (['paiement', 'montantAvance', 'montantPaye'].includes(fieldKey)) {
    return 'paiement';
  }
  return 'autre';
}

function groupChangeLogsByBatch(logs: ChangeLogEntry[]) {
  const m = new Map<string, ChangeLogEntry[]>();
  for (const log of logs) {
    const k = log.createdAt;
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(log);
  }
  return Array.from(m.entries()).sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());
}

/** Identifiant client et champs contact en doublon de contactInfo — masqués pour l’agence. */
const HIDDEN_CHANGELOG_KEYS = new Set(['clientId', 'nom', 'prenom', 'telephone', 'mail']);

function filterVisibleLogEntries(entries: ChangeLogEntry[]): ChangeLogEntry[] {
  return entries.filter((e) => !HIDDEN_CHANGELOG_KEYS.has(e.fieldKey));
}

const PAYMENT_LOG_KEYS = new Set(['paiement', 'montantAvance', 'montantPaye', 'totalPrice']);

const PAIEMENT_MODE_LABEL: Record<string, string> = {
  paye: 'Payé intégralement',
  avance: 'Acompte / avance',
  non_paye: 'Non payé',
};

function formatDa(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toLocaleString('fr-FR')} DA`;
}

/** Regroupe les entrées d’un lot par catégorie, ordre fixe pour l’agence. */
function groupEntriesByCategory(items: ChangeLogEntry[]): [FieldCategory, ChangeLogEntry[]][] {
  const bucket: Record<FieldCategory, ChangeLogEntry[]> = {
    statuts: [],
    forfait: [],
    voyageurs: [],
    contact: [],
    paiement: [],
    autre: [],
  };
  for (const e of items) {
    bucket[getCategory(e.fieldKey)].push(e);
  }
  return CATEGORY_ORDER.map((cat) => [cat, bucket[cat]] as [FieldCategory, ChangeLogEntry[]]).filter(
    ([, arr]) => arr.length > 0
  );
}

function looksLikeJson(s: string): boolean {
  const t = s.trim();
  return (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'));
}

function tryParseJson(raw: string): unknown {
  if (!raw || !String(raw).trim()) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

const ROOM_TYPE_FR: Record<string, string> = {
  single: 'Single',
  double: 'Double',
  triple: 'Triple',
  quadruple: 'Quadruple',
  quintuple: 'Quintuple',
};

/** Affichage lisible pour l’agence (liste de voyageurs, bébés, chambres, contact). Retourne null → fallback texte / JSON. */
function renderFriendlyValue(fieldKey: string, raw: string): React.ReactNode | null {
  const v = raw?.trim() ?? '';
  switch (fieldKey) {
    case 'personnes':
      return renderPersonnesBlock(v);
    case 'bebes':
      return renderBebesBlock(v);
    case 'chambres':
      return renderChambresBlock(v);
    case 'contactInfo':
      return renderContactInfoBlock(v);
    default:
      return null;
  }
}

function renderPersonnesBlock(raw: string): React.ReactNode | null {
  const parsed = tryParseJson(raw);
  if (!Array.isArray(parsed)) return null;
  if (parsed.length === 0) {
    return <p className="text-sm text-gray-600">Aucun voyageur</p>;
  }
  return (
    <ul className="space-y-0 divide-y divide-black/[0.07]">
      {parsed.map((item, i) => {
        const p = item as Record<string, unknown>;
        const prenom = String(p.prenom ?? '').trim();
        const nom = String(p.nom ?? '').trim();
        const name = [prenom, nom].filter(Boolean).join(' ') || `Participant ${i + 1}`;
        const type = String(p.type ?? 'adulte').toLowerCase();
        const typeFr = type === 'enfant' ? 'Enfant' : type === 'adulte' ? 'Adulte' : type;
        const ch =
          p.chambre != null && p.chambre !== '' ? ` · Chambre ${Number(p.chambre)}` : '';
        return (
          <li key={i} className="flex gap-3 py-2.5 first:pt-0 last:pb-0">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black/[0.06] text-xs font-bold tabular-nums text-gray-800">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gray-900 leading-snug">{name}</p>
              <p className="text-xs text-gray-600 mt-0.5">
                {typeFr}
                {ch}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function renderBebesBlock(raw: string): React.ReactNode | null {
  const parsed = tryParseJson(raw);
  if (!Array.isArray(parsed)) return null;
  if (parsed.length === 0) {
    return <p className="text-sm text-gray-600">Aucun bébé</p>;
  }
  return (
    <ul className="space-y-0 divide-y divide-black/[0.07]">
      {parsed.map((item, i) => {
        const b = item as Record<string, unknown>;
        const prenom = String(b.prenom ?? '').trim();
        const nom = String(b.nom ?? '').trim();
        const name = [prenom, nom].filter(Boolean).join(' ') || `Bébé ${i + 1}`;
        const ch =
          b.chambre != null && b.chambre !== '' ? `Chambre ${Number(b.chambre)}` : null;
        return (
          <li key={i} className="flex gap-3 py-2.5 first:pt-0 last:pb-0">
            <Baby className="w-5 h-5 shrink-0 text-amber-700/90" aria-hidden />
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 leading-snug">{name}</p>
              {ch ? <p className="text-xs text-gray-600 mt-0.5">{ch}</p> : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function renderChambresBlock(raw: string): React.ReactNode | null {
  const parsed = tryParseJson(raw);
  if (!Array.isArray(parsed)) return null;
  if (parsed.length === 0) {
    return <p className="text-sm text-gray-600">Aucune chambre</p>;
  }
  return (
    <ol className="space-y-2">
      {parsed.map((item, i) => {
        const c = item as Record<string, unknown>;
        const t = String(c.type ?? '').toLowerCase();
        const label = ROOM_TYPE_FR[t] || t || '—';
        return (
          <li key={i} className="flex items-baseline gap-2 text-sm">
            <span className="font-semibold text-gray-800 tabular-nums shrink-0">Chambre {i + 1}</span>
            <span className="text-gray-500">—</span>
            <span className="font-medium text-gray-900">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function renderContactInfoBlock(raw: string): React.ReactNode | null {
  const parsed = tryParseJson(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const o = parsed as Record<string, unknown>;
  const lines: { k: string; v: string }[] = [];
  const nom = String(o.nom ?? '').trim();
  const prenom = String(o.prenom ?? '').trim();
  if (prenom || nom) {
    lines.push({ k: 'Identité', v: [prenom, nom].filter(Boolean).join(' ') });
  }
  const tel = String(o.telephone ?? o.phone ?? '').trim();
  if (tel) lines.push({ k: 'Téléphone', v: tel });
  const mail = String(o.mail ?? o.email ?? '').trim();
  if (mail) lines.push({ k: 'E-mail', v: mail });
  if (lines.length === 0) return null;
  return (
    <dl className="space-y-2 text-sm">
      {lines.map(({ k, v }) => (
        <div key={k}>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{k}</dt>
          <dd className="mt-0.5 font-medium text-gray-900 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function formatSessionHeading(iso: string): { line1: string; line2: string; short: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return { line1: 'Date inconnue', line2: '', short: '' };
  }
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThat = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfToday.getTime() - startOfThat.getTime()) / 86400000);

  const timeStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const dateLong = d.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  let line1: string;
  if (diffDays === 0) line1 = `Aujourd’hui · ${timeStr}`;
  else if (diffDays === 1) line1 = `Hier · ${timeStr}`;
  else if (diffDays === 2) line1 = `Avant-hier · ${timeStr}`;
  else line1 = dateLong.charAt(0).toUpperCase() + dateLong.slice(1) + ` · ${timeStr}`;

  const line2 = d.toLocaleString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const short = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) + ` · ${timeStr}`;

  return { line1, line2, short };
}

function CategoryIcon({ category }: { category: FieldCategory }) {
  const cls = 'w-4 h-4 shrink-0 text-indigo-600';
  switch (category) {
    case 'paiement':
      return <CreditCard className={cls} />;
    case 'voyageurs':
      return <Users className={cls} />;
    case 'contact':
      return <UserCircle className={cls} />;
    case 'forfait':
      return <Package className={cls} />;
    case 'statuts':
      return <ClipboardList className={cls} />;
    default:
      return <FileText className={cls} />;
  }
}

function FieldRowIcon({ fieldKey }: { fieldKey: string }) {
  if (fieldKey === 'specialRequests') return <MessageSquare className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />;
  if (fieldKey === 'delaiOption') return <Clock className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />;
  return null;
}

function ValueBlock({
  label,
  value,
  variant,
  fieldKey,
}: {
  label: string;
  value: string;
  variant: 'before' | 'after';
  fieldKey?: string;
}) {
  const base =
    variant === 'before'
      ? 'bg-rose-50/90 border-rose-100 text-rose-950'
      : 'bg-emerald-50/90 border-emerald-100 text-emerald-950';

  const friendly = fieldKey ? renderFriendlyValue(fieldKey, value) : null;
  if (friendly !== null) {
    return (
      <div className={`rounded-lg border px-3 py-3 ${base}`}>
        <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80 mb-2">{label}</p>
        {friendly}
      </div>
    );
  }

  const isLong = value.length > 120 || looksLikeJson(value);

  if (isLong) {
    return (
      <div className={`rounded-lg border px-3 py-2 min-h-[2.5rem] ${base}`}>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">{label}</p>
        <details className="group">
          <summary className="cursor-pointer list-none flex items-center gap-1 text-xs font-medium text-gray-700">
            <ChevronDown className="w-3.5 h-3.5 shrink-0 transition-transform group-open:rotate-180" />
            Voir le détail technique ({value.length} caractères)
          </summary>
          <pre className="mt-2 text-[11px] leading-relaxed whitespace-pre-wrap break-words max-h-48 overflow-y-auto font-mono bg-white/60 rounded p-2 border border-black/5">
            {value || '—'}
          </pre>
        </details>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border px-3 py-2.5 min-h-[2.75rem] flex flex-col justify-center ${base}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70 mb-0.5">{label}</p>
      <p className="text-sm font-medium leading-snug break-words">{value || '—'}</p>
    </div>
  );
}

export default function BookingHistoryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [historyTab, setHistoryTab] = useState<'paiements' | 'modifications'>('paiements');
  const bookingIdNum = id && !isNaN(Number(id)) ? parseInt(id, 10) : 0;

  const { data: bookingData, loading: loadingBooking, error: bookingError } = useQuery(GET_BOOKING, {
    variables: { id: bookingIdNum },
    skip: !bookingIdNum,
  });
  const { data: logsData, loading: loadingLogs } = useQuery(GET_BOOKING_CHANGE_LOGS, {
    variables: { bookingId: bookingIdNum },
    skip: !bookingIdNum,
  });

  const booking = bookingData?.booking;
  const pkg = booking?.package;

  const changeLogGroups = useMemo(() => {
    const logs = (logsData?.bookingChangeLogs || []) as ChangeLogEntry[];
    return groupChangeLogsByBatch(logs);
  }, [logsData]);

  const visibleChangeLogGroups = useMemo(() => {
    return changeLogGroups
      .map(([createdAt, items]) => [createdAt, filterVisibleLogEntries(items)] as const)
      .filter(([, items]) => items.length > 0);
  }, [changeLogGroups]);

  const stats = useMemo(() => {
    const totalSessions = visibleChangeLogGroups.length;
    const totalChanges = visibleChangeLogGroups.reduce((acc, [, items]) => acc + items.length, 0);
    const lastIso = visibleChangeLogGroups[0]?.[0];
    return { totalSessions, totalChanges, lastIso };
  }, [visibleChangeLogGroups]);

  /** Lignes d’historique liées au paiement (ordre récent → ancien). */
  const paymentLogEntries = useMemo(() => {
    const logs = (logsData?.bookingChangeLogs || []) as ChangeLogEntry[];
    return logs
      .filter((e) => PAYMENT_LOG_KEYS.has(e.fieldKey))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [logsData]);

  if (!id || isNaN(Number(id))) {
    return (
      <AdminLayout>
        <div className="max-w-3xl mx-auto p-6 text-center text-red-600">Identifiant de réservation invalide.</div>
      </AdminLayout>
    );
  }

  if (loadingBooking) {
    return (
      <AdminLayout>
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-6 animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-40 bg-gray-100 rounded-xl" />
          <div className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </AdminLayout>
    );
  }

  if (bookingError || !booking) {
    return (
      <AdminLayout>
        <div className="max-w-3xl mx-auto p-6 text-center text-red-600">Réservation introuvable.</div>
      </AdminLayout>
    );
  }

  const clientsPath = pkg?.id ? `/admin/packages/${pkg.id}/clients` : '/admin/bookings';
  const clientLabel =
    booking.nom || booking.prenom
      ? [booking.prenom, booking.nom].filter(Boolean).join(' ')
      : booking.client
        ? [booking.client.firstName, booking.client.lastName].filter(Boolean).join(' ')
        : '—';

  const paiementLabel = (() => {
    const p = booking.paiement;
    if (p === 'avance' && booking.montantAvance != null) {
      return `Avance · ${Number(booking.montantAvance).toLocaleString('fr-FR')} DA`;
    }
    if (p === 'paye') return 'Payé';
    if (p === 'non_paye') return 'Non payé';
    return p || '—';
  })();

  const totalPrixSejour = Number(booking.totalPrice) || 0;
  const montantPayeNum =
    booking.montantPaye != null && !Number.isNaN(Number(booking.montantPaye)) ? Number(booking.montantPaye) : 0;
  const montantAvanceNum =
    booking.montantAvance != null && !Number.isNaN(Number(booking.montantAvance))
      ? Number(booking.montantAvance)
      : null;

  /** Règle agence : en mode « avance », le montant d’avance = ce qui est déjà versé (pas un objectif séparé). */
  const montantDejaVerse = (() => {
    const mode = String(booking.paiement || '');
    if (mode === 'avance') {
      if (montantAvanceNum != null) return montantAvanceNum;
      return montantPayeNum;
    }
    if (mode === 'paye') {
      return totalPrixSejour > 0 ? totalPrixSejour : montantPayeNum;
    }
    return montantPayeNum;
  })();

  const resteAPayer = Math.max(0, totalPrixSejour - montantDejaVerse);
  const pourcentageEncaisse =
    totalPrixSejour > 0 ? Math.min(100, Math.round((montantDejaVerse / totalPrixSejour) * 100)) : 0;

  const modePaiementKey = String(booking.paiement || '');
  const modePaiementLibelle = PAIEMENT_MODE_LABEL[modePaiementKey] || modePaiementKey || '—';

  const dejaVerseLegende =
    modePaiementKey === 'avance'
      ? 'Montant d’avance saisi : chez vous il correspond au versement déjà pris en compte.'
      : modePaiementKey === 'paye'
        ? 'Séjour considéré comme entièrement réglé.'
        : 'Somme enregistrée dans « montant payé » (hors mode avance).';

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-6 px-4 sm:px-6 pb-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 w-fit text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/admin/bookings/edit/${bookingIdNum}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-amber-950 text-sm font-medium hover:bg-amber-100 transition-colors shadow-sm"
            >
              <Pencil className="w-4 h-4" />
              Modifier la réservation
            </Link>
            <Link
              to={clientsPath}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-800 text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm"
            >
              <Package className="w-4 h-4 text-gray-500" />
              Fiche groupe / voyage
            </Link>
          </div>
        </div>

        {/* Aperçu réservation — repères rapides pour l’agence */}
        <section className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/90 via-white to-white p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700/90 mb-1">
                Historique du dossier
              </p>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                {pkg?.title || 'Réservation'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Dossier n° <span className="font-mono font-semibold text-gray-800">{booking.id}</span>
                {booking.statutReservation ? (
                  <span className="text-gray-400 mx-1.5">·</span>
                ) : null}
                {booking.statutReservation ? (
                  <span className="capitalize">{booking.statutReservation}</span>
                ) : null}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 border border-indigo-100 px-3 py-1.5 text-xs font-medium text-gray-800 shadow-sm">
                <UserCircle className="w-3.5 h-3.5 text-indigo-600" />
                {clientLabel}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 border border-indigo-100 px-3 py-1.5 text-xs font-medium text-gray-800 shadow-sm">
                <CreditCard className="w-3.5 h-3.5 text-indigo-600" />
                {paiementLabel}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4 leading-relaxed max-w-3xl">
            Utilisez les onglets ci-dessous : <strong>Suivi des paiements</strong> pour les montants et mouvements,{' '}
            <strong>Détail des modifications</strong> pour l’historique des champs (voyageurs, séjour, etc.) avec avant /
            après.
          </p>
        </section>

        <section
          className="rounded-2xl border border-gray-200/90 bg-white shadow-sm overflow-hidden"
          aria-label="Historique — onglets"
        >
          <div
            className="flex flex-col sm:flex-row gap-1.5 p-2 sm:p-2.5 bg-gradient-to-r from-slate-50/95 to-gray-50/90 border-b border-gray-200"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={historyTab === 'paiements'}
              onClick={() => setHistoryTab('paiements')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                historyTab === 'paiements'
                  ? 'bg-white text-emerald-900 shadow-md ring-1 ring-emerald-200/90'
                  : 'text-gray-600 hover:bg-white/80 hover:text-gray-900'
              }`}
            >
              <Wallet className="w-4 h-4 shrink-0" />
              Suivi des paiements
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={historyTab === 'modifications'}
              onClick={() => setHistoryTab('modifications')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                historyTab === 'modifications'
                  ? 'bg-white text-indigo-900 shadow-md ring-1 ring-indigo-200/90'
                  : 'text-gray-600 hover:bg-white/80 hover:text-gray-900'
              }`}
            >
              <History className="w-4 h-4 shrink-0" />
              Détail des modifications
            </button>
          </div>

          {historyTab === 'paiements' && (
            <div className="bg-gradient-to-br from-emerald-50/95 via-white to-teal-50/40 border-t border-emerald-100/40">
          <div className="px-5 py-4 sm:px-6 border-b border-emerald-100/90 bg-white/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-emerald-600 text-white p-2.5 shadow-sm">
                <Wallet className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 tracking-tight">Suivi des paiements</h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  En mode <strong>avance</strong>, le montant d’avance est celui que vous considérez comme{' '}
                  <strong>déjà versé</strong> par le client. Le reste à encaisser part du prix total du séjour.
                </p>
              </div>
            </div>
            <span
              className={`inline-flex self-start sm:self-center items-center rounded-full px-3 py-1 text-xs font-semibold border ${
                modePaiementKey === 'paye'
                  ? 'bg-emerald-100 text-emerald-900 border-emerald-200'
                  : modePaiementKey === 'avance'
                    ? 'bg-amber-100 text-amber-950 border-amber-200'
                    : 'bg-slate-100 text-slate-800 border-slate-200'
              }`}
            >
              {modePaiementLibelle}
            </span>
          </div>

          <div className="p-5 sm:p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="rounded-xl border border-white/80 bg-white/90 p-4 shadow-sm ring-1 ring-emerald-100/60">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Prix total du séjour</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900 tabular-nums">{formatDa(totalPrixSejour)}</p>
              </div>
              <div className="rounded-xl border border-white/80 bg-white/90 p-4 shadow-sm ring-1 ring-emerald-100/60">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Déjà versé</p>
                <p className="text-lg sm:text-xl font-bold text-emerald-800 tabular-nums">{formatDa(montantDejaVerse)}</p>
                <p className="text-[11px] text-gray-500 mt-1.5 leading-snug">{dejaVerseLegende}</p>
              </div>
              <div className="rounded-xl border border-white/80 bg-white/90 p-4 shadow-sm ring-1 ring-amber-100/80">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Reste à encaisser</p>
                <p
                  className={`text-lg sm:text-xl font-bold tabular-nums ${
                    resteAPayer > 0 ? 'text-amber-900' : 'text-emerald-700'
                  }`}
                >
                  {formatDa(resteAPayer)}
                </p>
                <p className="text-[11px] text-gray-500 mt-1.5 leading-snug">Prix total − déjà versé</p>
              </div>
            </div>

            {totalPrixSejour > 0 && (
              <div>
                <div className="flex justify-between text-xs text-gray-600 mb-1.5">
                  <span>Encaissement</span>
                  <span className="font-semibold text-emerald-800">{pourcentageEncaisse} %</span>
                </div>
                <div className="h-3 rounded-full bg-emerald-100/80 overflow-hidden border border-emerald-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
                    style={{ width: `${pourcentageEncaisse}%` }}
                  />
                </div>
              </div>
            )}

            <div className="rounded-xl border border-emerald-100 bg-white/80">
              <div className="px-4 py-3 border-b border-emerald-50 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-emerald-700" />
                <h3 className="text-sm font-semibold text-gray-900">Mouvements enregistrés (paiement)</h3>
              </div>
              <div className="p-4 max-h-[min(420px,55vh)] overflow-y-auto">
                {loadingLogs ? (
                  <p className="text-sm text-gray-500">Chargement…</p>
                ) : paymentLogEntries.length === 0 ? (
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Aucun mouvement de paiement dans l’historique pour l’instant. Les changements de mode, d’acompte, de
                    montant payé ou de prix total apparaîtront ici après les prochaines modifications de la réservation.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {paymentLogEntries.map((entry) => (
                      <li
                        key={entry.id}
                        className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5 text-sm"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 gap-y-1">
                          <span className="font-medium text-gray-900">{entry.label}</span>
                          <time
                            className="text-xs text-gray-500 tabular-nums"
                            dateTime={entry.createdAt}
                          >
                            {new Date(entry.createdAt).toLocaleString('fr-FR', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </time>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm">
                          <span className="text-rose-800/90 line-through decoration-rose-300/90">
                            {entry.oldValue || '—'}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="font-semibold text-emerald-900">{entry.newValue || '—'}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <p className="text-xs text-gray-500 flex items-start gap-2">
              <CreditCard className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
              Pour mettre à jour les montants ou le mode de paiement, utilisez « Modifier la réservation » — chaque
              sauvegarde apparaît ici et dans l’onglet « Détail des modifications ».
            </p>
          </div>
            </div>
          )}

          {historyTab === 'modifications' && (
            <div className="border-t border-gray-100 bg-white">
        {/* Stats */}
        {!loadingLogs && visibleChangeLogGroups.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 sm:p-5 border-b border-gray-100 bg-slate-50/50">
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm flex items-center gap-3">
              <div className="rounded-lg bg-violet-100 p-2 text-violet-700">
                <CalendarDays className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSessions}</p>
                <p className="text-xs text-gray-500">Sauvegarde{stats.totalSessions > 1 ? 's' : ''} enregistrée{stats.totalSessions > 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm flex items-center gap-3">
              <div className="rounded-lg bg-sky-100 p-2 text-sky-700">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalChanges}</p>
                <p className="text-xs text-gray-500">Champ{stats.totalChanges > 1 ? 's' : ''} modifié{stats.totalChanges > 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm flex items-center gap-3 sm:col-span-1">
              <div className="rounded-lg bg-amber-100 p-2 text-amber-800">
                <History className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {stats.lastIso ? formatSessionHeading(stats.lastIso).short : '—'}
                </p>
                <p className="text-xs text-gray-500">Dernière activité</p>
              </div>
            </div>
          </div>
        )}

              <div className="px-5 sm:px-6 py-5 sm:py-6">
            {loadingLogs ? (
              <div className="space-y-4 animate-pulse">
                <div className="h-24 bg-gray-100 rounded-xl" />
                <div className="h-24 bg-gray-100 rounded-xl" />
              </div>
            ) : visibleChangeLogGroups.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 px-6 py-12 text-center">
                <div className="inline-flex rounded-full bg-gray-100 p-4 mb-4">
                  <History className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-800 font-medium">Aucun historique pour l’instant</p>
                <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                  Dès qu’une modification est enregistrée depuis la page « Modifier la réservation », elle apparaîtra ici
                  avec la date et les valeurs avant / après.
                </p>
                <Link
                  to={`/admin/bookings/edit/${bookingIdNum}`}
                  className="inline-flex mt-6 items-center gap-2 text-sm font-medium text-indigo-700 hover:text-indigo-900"
                >
                  <Pencil className="w-4 h-4" />
                  Ouvrir la modification de la réservation
                </Link>
              </div>
            ) : (
              <ol className="relative space-y-8">
                <div
                  className="absolute left-[11px] top-3 bottom-3 w-px bg-gradient-to-b from-indigo-200 via-indigo-100 to-transparent sm:left-[13px]"
                  aria-hidden
                />
                {visibleChangeLogGroups.map(([createdAt, items], sessionIndex) => {
                  const heading = formatSessionHeading(createdAt);
                  const byCat = groupEntriesByCategory(items);
                  return (
                    <li key={createdAt} className="relative pl-10 sm:pl-12">
                      <span
                        className="absolute left-0 top-1.5 flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full border-2 border-white bg-indigo-600 text-[10px] font-bold text-white shadow-sm ring-2 ring-indigo-100"
                        title={heading.line2}
                      >
                        {visibleChangeLogGroups.length - sessionIndex}
                      </span>
                      <article className="rounded-xl border border-gray-100 bg-gray-50/40 overflow-hidden shadow-sm">
                        <header className="px-4 py-3 sm:px-5 bg-white border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm sm:text-base font-semibold text-gray-900">{heading.line1}</p>
                            <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">{heading.line2}</p>
                          </div>
                          <span className="text-xs font-medium text-indigo-800 bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-full">
                            {items.length} modification{items.length > 1 ? 's' : ''}
                          </span>
                        </header>
                        <div className="p-4 sm:p-5 space-y-6">
                          {byCat.map(([category, entries]) => (
                            <div key={category}>
                              <div className="flex items-center gap-2 mb-3">
                                <CategoryIcon category={category} />
                                <h3 className="text-xs font-bold uppercase tracking-wide text-gray-600">
                                  {CATEGORY_LABEL[category]}
                                </h3>
                              </div>
                              <ul className="space-y-4">
                                {entries.map((entry) => (
                                  <li
                                    key={entry.id}
                                    className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
                                  >
                                    {entry.fieldKey !== 'contactInfo' ? (
                                      <div className="flex items-start gap-2 mb-3">
                                        <FieldRowIcon fieldKey={entry.fieldKey} />
                                        <p className="text-sm font-semibold text-gray-900 leading-snug">{entry.label}</p>
                                      </div>
                                    ) : null}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                      <ValueBlock
                                        label="Avant"
                                        value={entry.oldValue || ''}
                                        variant="before"
                                        fieldKey={entry.fieldKey}
                                      />
                                      <ValueBlock
                                        label="Après"
                                        value={entry.newValue || ''}
                                        variant="after"
                                        fieldKey={entry.fieldKey}
                                      />
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ol>
            )}
              </div>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
